import { useEffect } from 'react';
import { LocationType, useFrontendStatus, useFrontendSubscriptionStatus } from '@cmctechnology/webinvest-store-frontend';
import { useDispatch, useSelector } from 'react-redux';
import { WATCHLISTS_STORE_NAME, WatchlistsStore } from './store';
import { WATCHLIST_API_NAMES } from './apiNames';
import { watchlistsActions, WatchlistSystem } from './index';
import { SingleValue } from 'react-select';

type IStore = ReturnType<typeof WatchlistsStore.getState>;

export const useSubscribeWatchlists = (location: LocationType) => {
  const dispatch = useDispatch();
  const watchlists = useSelector((store: IStore) => store.watchlists.lists);
  const { polling, error, fetching } = useFrontendSubscriptionStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListsGet1);

  useEffect(() => {
    dispatch<any>(watchlistsActions.subscribeWatchlists(location));
    return () => {
      dispatch<any>(watchlistsActions.unsubscribeWatchlists(location));
    };
  }, []);

  return {
    loading: fetching && !watchlists?.length,
    polling,
    error,
    data: watchlists,
    fetching
  };
};

export const useWatchlistsAsSelect = (location: LocationType) => {
  const lists = useSelector((store: IStore) => store.watchlists.lists);
  const selectedWatchlist = useSelector((store: IStore) => store.watchlists.selectedWatchlist);
  const dispatch = useDispatch();

  const onChange = (selected: SingleValue<{ label: string; value: number }>) => {
    dispatch<any>(watchlistsActions.selectWatchlist(location, selected!.value));
  };

  const options = lists?.map(({ name, id }) => ({
    label: name,
    value: id
  }));

  const selected = (location && selectedWatchlist[location]) || '';
  const value = options?.find((option) => selected === option.value) || options?.[0];
  return { options, value, onChange };
};

export const useSubscribeWatchlistItems = (location: LocationType, selectedId?: number) => {
  const dispatch = useDispatch();
  const watchlists = useSelector((store: IStore) => store.watchlists.lists);
  const selectedWatchlist = useSelector((store: IStore) => store.watchlists.selectedWatchlist[location]);
  const activeWatchlist =
    // 1. Fetch by selectID
    selectedId ??
    // 2. Check location if selectedWatchlist is stored
    selectedWatchlist ??
    // 3. use first watchlist
    watchlists?.[0]?.id ??
    // 4. nothing
    undefined;

  const { polling, error, fetching } = useFrontendSubscriptionStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.WatchListGet3, activeWatchlist);

  const watchlistItems = useSelector((store: IStore) => (activeWatchlist ? store.watchlists.items?.[activeWatchlist] : undefined));

  useEffect(() => {
    if (activeWatchlist) {
      dispatch<any>(watchlistsActions.subscribeWatchlistItems(location, activeWatchlist));
    }

    return () => {
      if (activeWatchlist) {
        dispatch<any>(watchlistsActions.unsubscribeWatchlistItems(location, activeWatchlist));
      }
    };
  }, [activeWatchlist]);

  return {
    loading: fetching && !watchlistItems,
    polling,
    error,
    data: watchlistItems,
    fetching
  };
};

export const useSubscribeThematicWatchlists = (location: LocationType) => {
  const dispatch = useDispatch();
  const thematicWatchlists = useSelector((store: IStore) => store.watchlists.thematicLists);
  const { polling, error, fetching } = useFrontendSubscriptionStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.ThematicWatchlistsGet1);

  useEffect(() => {
    dispatch<any>(watchlistsActions.subscribeThematicWatchlists(location));
    return () => {
      dispatch<any>(watchlistsActions.unsubscribeThematicWatchlists(location));
    };
  }, []);

  return {
    loading: fetching && !thematicWatchlists?.length,
    polling,
    error,
    data: thematicWatchlists,
    fetching
  };
};

export const useSubscribeThematicWatchlist = (location: LocationType, selectedId: number) => {
  const dispatch = useDispatch();

  const { polling, error, fetching } = useFrontendSubscriptionStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.ThematicWatchlistGet1, selectedId);

  const watchlist = useSelector((store: IStore) => (selectedId ? store.watchlists.thematicItems?.[selectedId] : undefined));

  useEffect(() => {
    dispatch<any>(watchlistsActions.subscribeThematicWatchlist(location, selectedId));

    return () => {
      dispatch<any>(watchlistsActions.unsubscribeThematicWatchlist(location, selectedId));
    };
  }, [selectedId]);

  return {
    loading: fetching && !watchlist,
    polling,
    error,
    data: watchlist,
    fetching
  };
};

export const useOptoWatchlists = (location: LocationType) => {
  const dispatch = useDispatch();
  const optoWatchlists = useSelector((store: IStore) => store.watchlists.optoWatchlists);
  const { fetching, ...rest } = useFrontendStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.ThematicWatchlistsGet1, location);

  useEffect(() => {
    dispatch<any>(watchlistsActions.fetchOptoWatchlists(location));
  }, []);

  return {
    loading: fetching && !optoWatchlists?.length,
    fetching,
    data: optoWatchlists,
    ...rest
  };
};

export const useOptoWatchlist = (location: LocationType, selectedId: number) => {
  const dispatch = useDispatch();
  const storeKey = `${location}_${selectedId}`;
  const { fetching, ...rest } = useFrontendStatus(WATCHLISTS_STORE_NAME, WATCHLIST_API_NAMES.ThematicWatchlistGet1, storeKey);
  const optoWatchlist = useSelector((store: IStore) => (selectedId ? store.watchlists.optoItems?.[selectedId] : undefined));

  useEffect(() => {
    dispatch<any>(watchlistsActions.fetchOptoWatchlist(selectedId, storeKey));
  }, [selectedId]);

  return {
    loading: fetching && !optoWatchlist,
    fetching,
    data: optoWatchlist,
    ...rest
  };
};

export const useThematicWatchlist = <TSystem extends WatchlistSystem>(
  location: LocationType,
  selectedId: TSystem extends 'standard' ? number : string,
  watchlistSystem: TSystem = 'standard' as TSystem
) => {
  const dispatch = useDispatch();
  const storeKey = `${location}_${selectedId}`;

  const apiName = watchlistSystem === 'cloud' ? WATCHLIST_API_NAMES.CloudThematicWatchlistGet1 : WATCHLIST_API_NAMES.ThematicWatchlistGet1;

  const { fetching, ...rest } = useFrontendStatus(WATCHLISTS_STORE_NAME, apiName, storeKey);
  const thematicWatchlist = useSelector((store: IStore) => store.watchlists.thematicItems?.[selectedId]);

  useEffect(() => {
    dispatch<any>(
      watchlistSystem === 'cloud'
        ? watchlistsActions.fetchCloudThematicWatchlist(selectedId as string)
        : watchlistsActions.fetchThematicWatchlist(selectedId as number, false)
    );
  }, [selectedId]);

  return {
    loading: fetching && !thematicWatchlist,
    fetching,
    data: thematicWatchlist,
    ...rest
  };
};

export const useThematicWatchlists = (location: LocationType, watchlistSystem: WatchlistSystem = 'standard') => {
  const dispatch = useDispatch();
  const thematicWatchlists = useSelector((store: IStore) => store.watchlists.thematicLists);
  const apiName = watchlistSystem === 'cloud' ? WATCHLIST_API_NAMES.CloudThematicWatchlistsGet1 : WATCHLIST_API_NAMES.ThematicWatchlistsGet1;
  const { fetching, ...rest } = useFrontendStatus(WATCHLISTS_STORE_NAME, apiName, location);

  useEffect(() => {
    dispatch<any>(watchlistSystem === 'cloud' ? watchlistsActions.fetchCloudThematicWatchlists() : watchlistsActions.fetchThematicWatchlists());
  }, []);

  return {
    loading: fetching && !thematicWatchlists?.length,
    fetching,
    data: thematicWatchlists,
    ...rest
  };
};
